import {
  DeliveryStatus,
  DisputeStatus,
  FinancialStatus,
  ListingStatus,
  UploadStatus,
  TicketStatus,
  TicketPriority,
  OfferStatus,
} from "@hybrid-escrow/shared";

type Tone = "neutral" | "success" | "warning" | "danger" | "info";

const FINANCIAL_TONE: Record<string, Tone> = {
  [FinancialStatus.PENDING_FUNDING]: "warning",
  [FinancialStatus.FUNDED]: "success",
  [FinancialStatus.DISPUTED]: "danger",
  [FinancialStatus.RELEASED]: "success",
  [FinancialStatus.REFUNDED]: "info",
  [FinancialStatus.EXPIRED]: "neutral",
  [FinancialStatus.FAILED]: "danger",
};

const DELIVERY_TONE: Record<string, Tone> = {
  [DeliveryStatus.NOT_ENTITLED]: "neutral",
  [DeliveryStatus.AVAILABLE]: "success",
  [DeliveryStatus.DOWNLOADED]: "info",
  [DeliveryStatus.REVOKED]: "danger",
};

const LISTING_TONE: Record<string, Tone> = {
  [ListingStatus.DRAFT]: "neutral",
  [ListingStatus.SCANNING]: "warning",
  [ListingStatus.READY]: "info",
  [ListingStatus.PUBLISHED]: "success",
  [ListingStatus.REJECTED]: "danger",
  [ListingStatus.ARCHIVED]: "neutral",
};

const UPLOAD_TONE: Record<string, Tone> = {
  [UploadStatus.PRESIGNED]: "neutral",
  [UploadStatus.UPLOADED]: "info",
  [UploadStatus.SCANNING]: "warning",
  [UploadStatus.CLEAN]: "success",
  [UploadStatus.INFECTED]: "danger",
  [UploadStatus.FAILED]: "danger",
  [UploadStatus.DELETED]: "neutral",
};

const DISPUTE_TONE: Record<string, Tone> = {
  [DisputeStatus.PENDING_ONCHAIN]: "warning",
  [DisputeStatus.OPEN]: "warning",
  [DisputeStatus.UNDER_REVIEW]: "info",
  [DisputeStatus.RESOLVED]: "success",
  [DisputeStatus.REJECTED]: "danger",
};

const TICKET_STATUS_TONE: Record<string, Tone> = {
  [TicketStatus.NEW]: "info",
  [TicketStatus.PENDING]: "warning",
  [TicketStatus.RESOLVED]: "success",
  [TicketStatus.CANCELLED]: "neutral",
};

const TICKET_PRIORITY_TONE: Record<string, Tone> = {
  [TicketPriority.CRITICAL]: "danger",
  [TicketPriority.HIGH]: "warning",
  [TicketPriority.NORMAL]: "info",
  [TicketPriority.LOW]: "neutral",
};

const OFFER_STATUS_TONE: Record<string, Tone> = {
  [OfferStatus.PENDING]: "warning",
  [OfferStatus.COUNTERED]: "warning",
  [OfferStatus.ACCEPTED]: "success",
  [OfferStatus.DECLINED]: "danger",
  [OfferStatus.EXPIRED]: "neutral",
};

function Badge({ label, tone }: { label: string; tone: Tone }) {
  return <span className={`badge badge--${tone}`}>{label.replaceAll("_", " ")}</span>;
}

export const FinancialStatusBadge = ({ status }: { status: string }) => (
  <Badge label={status} tone={FINANCIAL_TONE[status] ?? "neutral"} />
);

export const DeliveryStatusBadge = ({ status }: { status: string }) => (
  <Badge label={status} tone={DELIVERY_TONE[status] ?? "neutral"} />
);

export const ListingStatusBadge = ({ status }: { status: string }) => (
  <Badge label={status} tone={LISTING_TONE[status] ?? "neutral"} />
);

export const UploadStatusBadge = ({ status }: { status: string }) => (
  <Badge label={status} tone={UPLOAD_TONE[status] ?? "neutral"} />
);

export const DisputeStatusBadge = ({ status }: { status: string }) => (
  <Badge label={status} tone={DISPUTE_TONE[status] ?? "neutral"} />
);

export const TicketStatusBadge = ({ status }: { status: string }) => (
  <Badge label={status} tone={TICKET_STATUS_TONE[status] ?? "neutral"} />
);

export const TicketPriorityBadge = ({ priority }: { priority: string }) => (
  <Badge label={priority} tone={TICKET_PRIORITY_TONE[priority] ?? "neutral"} />
);

export const OfferStatusBadge = ({ status }: { status: string }) => (
  <Badge label={status} tone={OFFER_STATUS_TONE[status] ?? "neutral"} />
);
